/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";

interface Props {
	onDispense: (status: string) => Promise<{
		message?: string;
	}>;
	dispenseStatus: string;
	onNext: () => void;

	isLimitReached: boolean;
	showToast: (
		message: string,
		toastType: "success" | "error"
	) => void;
}

const DispenseEntryFooter = ({
	onDispense,
	isLimitReached,
	dispenseStatus,
	onNext,
	showToast,
}: Props) => {
	const handleSubmit = async () => {
		try {
			if (dispenseStatus === "Completed") {
				onNext();
				return;
			}

			const response = await onDispense("Completed");

			// Validation failed or API failed
			if (!response) {
				return;
			}
			if (response?.message) {
				showToast(response.message, "success");
			}

			setTimeout(() => {
				onNext();
			}, 1200);

		} catch (error: any) {

			showToast(
				error?.response?.data?.message ||
				"An unexpected error occurred",
				"error"
			);
		}
	};

	const { t } = useTranslation();
	return (
		<div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-10 py-2 flex items-center justify-between shadow-[0_-20px_60px_-15px_rgba(0,0,0,0.1)]">
			<div className="flex gap-4">
				<button className="px-12 py-2 bg-white border border-slate-200 text-[#0F172A] rounded-2xl font-black text-xs uppercase tracking-[0.3em] hover:bg-slate-50 transition-all shadow-sm active:scale-95 leading-none">
					{t("mediaBatchInit.dispenseEntry.clear")}
				</button>
			</div>

			<div className="flex items-center gap-8">
				<button
					onClick={() => window.print()}
					className="bg-orange-600 text-white rounded-[2rem] px-14 py-1 shadow-[0_15px_40px_-10px_rgba(234,88,12,0.4)] flex items-center gap-6 hover:brightness-105 active:scale-[0.98] transition-all group"
				>
					<div className="w-7 h-7 rounded-xl bg-white/20 flex items-center justify-center group-hover:rotate-12 transition-transform">
						<span className="material-symbols-outlined text-xl font-black">
							print_connect
						</span>
					</div>
					<div className="text-left">
						<p className="text-[11px] font-black uppercase tracking-[0.2em] leading-none mb-1">
							{t("mediaBatchInit.dispenseEntry.commit")}
						</p>
						<p className="text-[9px] font-bold text-white/50 uppercase tracking-tight italic">
							{t("mediaBatchInit.dispenseEntry.commitSub")}
						</p>
					</div>
				</button>

				<button
					onClick={handleSubmit}
					className="bg-emerald-600 text-white rounded-[2rem] px-14 py-1 shadow-[0_15px_40px_-10px_rgba(5,150,105,0.4)] flex items-center gap-6 hover:brightness-105 active:scale-[0.98] transition-all group disabled:opacity-50"
				>
					<div className="w-7 h-7 rounded-xl bg-white/20 flex items-center justify-center group-hover:translate-x-1.5 transition-transform">
						<span className="material-symbols-outlined text-xl font-black">
							arrow_forward
						</span>
					</div>
					<div className="text-left">
						<p className="text-[11px] font-black uppercase tracking-[0.2em] leading-none mb-1">
							{t("mediaBatchInit.dispenseEntry.completeDispensing")}
						</p>
						<p className="text-[9px] font-bold text-white/50 uppercase tracking-tight italic">
							{t("mediaBatchInit.dispenseEntry.completeDispensingSub")}
						</p>
					</div>
				</button>

				<div className="h-12 w-px bg-slate-200 mx-4"></div>

				<div className="flex flex-col items-end">
					<p className="text-[10px] font-black text-slate-400 uppercase tracking-tight italic">
						21 CFR Part 11 compliant
					</p>
				</div>
			</div>
		</div>
	);
};

export default DispenseEntryFooter;
